'use client';

import { contentPipeline } from '@doist/content-system';
import { StarLarge } from '@doist/icons/brand';
import { Box, Carousel, Heading, Image, Text, useImage, VideoPlayer } from '@doist/marketist';
import { useTranslation } from 'app/i18n/client';
import { addProtocol } from 'app/services/contentful/add-protocol-to-url';
import { CTFRichText } from 'components/blog-posts/ctf-rich-text/ctf-rich-text';
import { useLocale } from 'next-intl';
import { isDefined } from 'services/is-defined';
import css from './details.module.css';
import type { Integration } from 'app/[locale]/integrations/types';
type DetailsProps = {
  integration: Integration;
};
const transform = contentPipeline({
  source: 'contentful'
});
function Details({
  integration
}: DetailsProps) {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, ['integrations', 'common']);
  const {
    features
  } = integration.fields;
  const {
    screenshots = [],
    about
  } = integration.fields.commonContent?.fields || {};
  return <Box className={css.main} data-sentry-element="Box" data-sentry-component="Details" data-sentry-source-file="details.tsx">
            {about && <CTFRichText document={transform(about)} />}

            {features && <Box tag="section" className={css.features}>
                    <Heading level="3" size="major-base" className="mbe-8">
                        {t('features')}
                    </Heading>
                    <Box tag="ul">{features.map(FeatureItem)}</Box>
                </Box>}

            {screenshots.length > 0 && <Box tag="section" className={css.carousel}>
                    <Carousel nextButtonLabel={t('next', {
        ns: 'common'
      })} previousButtonLabel={t('previous', {
        ns: 'common'
      })} dotIndicatorLabels={screenshots.map(s => s?.fields.title || '')} showBorders>
                        {screenshots.filter(isDefined).map((ss, i) => <ScreenshotImage key={ss.sys.id} title={ss.fields.title || ''} url={addProtocol(ss.fields.file?.url || '')} priority={i === 0} />)}
                    </Carousel>
                </Box>}
        </Box>;
}
function ScreenshotImage({
  title,
  url,
  priority
}: {
  title: string;
  url: string;
  priority?: boolean;
}) {
  const MEDIA_WIDTH = 650;
  const MEDIA_HEIGHT = 406;
  const {
    getImage
  } = useImage();
  const isDevAppEnv = process.env.NEXT_PUBLIC_APP_ENV === 'development';
  const imageUrl = new URL(url);
  const isGif = imageUrl.pathname.endsWith('.gif');

  // Gifs from Contentful can exceed Cloudinary's limits, so we request
  // as small a version as possible to reduce that possibility.
  if (imageUrl.hostname === 'images.ctfassets.net' && isGif) {
    imageUrl.searchParams.set('w', MEDIA_WIDTH.toString());
  }
  if (isGif && !isDevAppEnv) {
    return <VideoPlayer key={url} src={getImage(imageUrl.toString(), {
      format: 'f_auto:video'
    }).toString()} width={MEDIA_WIDTH} height={MEDIA_HEIGHT} muted playsInline autoPlay loop />;
  }
  return <Image width={MEDIA_WIDTH} height={MEDIA_HEIGHT} key={url} alt={title} src={url} loading={priority ? undefined : 'lazy'} priority={priority} className={css.screenshot} data-sentry-element="Image" data-sentry-component="ScreenshotImage" data-sentry-source-file="details.tsx" />;
}
function FeatureItem(text: string, i: number) {
  return <Box tag="li" key={i} className={css.copyContainer} data-sentry-element="Box" data-sentry-component="FeatureItem" data-sentry-source-file="details.tsx">
            <Box className={css.iconWrapper} data-sentry-element="Box" data-sentry-source-file="details.tsx">
                <StarLarge width={24} height={24} color="var(--color-td-primary)" data-sentry-element="StarLarge" data-sentry-source-file="details.tsx" />
            </Box>
            <Text size="body-base" data-sentry-element="Text" data-sentry-source-file="details.tsx">{text}</Text>
        </Box>;
}
export { Details };